import type { Theme, ThemeDefaultParams } from 'ag-grid-community'
import { themeQuartz } from 'ag-grid-community'

/**
 * AG Grid Theme Configuration
 *
 * This file contains ALL available AG Grid theme parameters organized by category.
 * Parameters currently in use are uncommented, while additional customization
 * options are commented out for easy discovery and activation.
 *
 * Categories:
 * - Global (colors, fonts, spacing, shadows)
 * - Headers
 * - Rows & Cells
 * - Selection & Range
 * - Borders & Dividers
 * - Inputs & Form Fields
 * - Buttons
 * - Checkboxes & Toggles
 * - Tabs
 * - Menus & Dropdowns
 * - Tooltips
 * - Sidebar & Panels
 * - Advanced Filter Builder
 * - Charts & Integrated Tools
 * - Drag & Drop
 * - Icons & Widgets
 * - Batch Edit Mode
 * - And many more...
 *
 * @see https://www.ag-grid.com/javascript-data-grid/theming-api/ for official documentation
 */

/**
 * Create grid theme with comprehensive customization options
 *
 * All parameters use AG Grid's official ThemeDefaultParams type, providing
 * full type safety and IDE autocomplete support.
 */
const createGridTheme = (): Theme =>
  themeQuartz.withParams({
    // ═══════════════════════════════════════════════════════════════
    // GLOBAL COLORS & APPEARANCE
    // ═══════════════════════════════════════════════════════════════

    /** Brand color for selections, focus outlines, and checkboxes */
    accentColor: 'var(--color-brand-500)',

    /** Main background color - many UI elements blend with this */
    backgroundColor: 'var(--color-white)',

    /** Default border color throughout the grid */
    borderColor: 'var(--color-slate-4)',

    /** Browser color scheme affecting scrollbars and form inputs */
    browserColorScheme: 'light' as const,

    /** Default text color */
    textColor: 'var(--color-slate-12)',

    /** Neutral color for UI elements (text, borders, backgrounds blend with this) */
    foregroundColor: 'var(--color-slate-11)',

    /** Color for less prominent text */
    subtleTextColor: 'var(--color-slate-10)',

    /** Color for invalid/error states */
    invalidColor: 'var(--color-red-9)',

    /** Background for non-data areas (headers, tool panels, menus) */
    chromeBackgroundColor: 'var(--color-slate-2)',

    // ═══════════════════════════════════════════════════════════════
    // TYPOGRAPHY
    // ═══════════════════════════════════════════════════════════════

    /** Default font family for all text */
    fontFamily: '"Keruj Sans", ui-sans-serif, system-ui, sans-serif',

    /** Default font size throughout the grid */
    fontSize: 12,

    /** Font size for data in grid rows */
    // dataFontSize: 12,

    // ═══════════════════════════════════════════════════════════════
    // LAYOUT & SPACING
    // ═══════════════════════════════════════════════════════════════

    /** Base spacing unit - all padding/margins are multiples of this */
    spacing: 6,

    /** Default corner radius for UI elements */
    // borderRadius: 4,

    /** Default border width */
    // borderWidth: 1,

    /** Corner radius of the grid wrapper */
    wrapperBorderRadius: 4,

    /** Border around the outside of the grid */
    // wrapperBorder: 'none',

    // ═══════════════════════════════════════════════════════════════
    // SHADOWS
    // ═══════════════════════════════════════════════════════════════

    /** Shadow for floating elements (dialogs, menus) */
    // popupShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',

    /** Shadow for elevated but attached elements (dropdowns, editors) */
    // cardShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',

    /** Shadow for dropdown menus */
    // dropdownShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',

    /** Shadow for dialogs (charts, advanced filter) */
    // dialogShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',

    /** Shadow for focused UI controls */
    // focusShadow: '0 0 0 3px rgba(var(--color-brand-500-rgb), 0.2)',

    /** Shadow for focused controls with validation errors */
    // focusErrorShadow: '0 0 0 3px rgba(var(--color-red-500-rgb), 0.2)',

    /** Shadow for cells being edited */
    cellEditingShadow: 'none',

    /** Shadow for menus */
    // menuShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',

    // ═══════════════════════════════════════════════════════════════
    // HEADERS
    // ═══════════════════════════════════════════════════════════════

    /** Header background color */
    headerBackgroundColor: 'var(--color-slate-2)',

    /** Header text color */
    headerTextColor: 'var(--color-slate-11)',

    /** Header font family (overrides fontFamily) */
    // headerFontFamily: 'Keruj Sans',

    /** Header font size */
    headerFontSize: 12,

    /** Header font weight */
    headerFontWeight: 600,

    /** Header row height (auto-calculated by default) */
    headerHeight: 32,

    /** Multiply header vertical padding */
    // headerVerticalPaddingScale: 1,

    /** Header cell hover background color */
    headerCellHoverBackgroundColor: 'var(--color-slate-3)',

    /** Header cell background when being dragged */
    // headerCellMovingBackgroundColor: 'var(--color-slate-4)',

    /** Duration of header background transitions */
    // headerCellBackgroundTransitionDuration: 0.2,

    /** Vertical borders between header columns */
    headerColumnBorder: true,

    /** Height of header column borders (percentage of header height) */
    // headerColumnBorderHeight: '100%',

    /** Color of column resize handle */
    // headerColumnResizeHandleColor: 'var(--color-slate-6)',

    /** Remove the decorative mark without changing the column's drag target. */
    headerColumnResizeHandleWidth: 0,

    /** Height of column resize handle */
    // headerColumnResizeHandleHeight: '100%',

    /** Borders between and below header rows */
    // headerRowBorder: true,

    // ═══════════════════════════════════════════════════════════════
    // ROWS & CELLS
    // ═══════════════════════════════════════════════════════════════

    /** Cell text color */
    cellTextColor: 'var(--color-slate-11)',

    /** Cell font family (overrides fontFamily) */
    // cellFontFamily: 'Keruj Sans',

    /** Row height (auto-calculated by default) */
    rowHeight: 32,

    /** Multiply row vertical padding */
    // rowVerticalPaddingScale: 1,

    /** Cell horizontal padding */
    // cellHorizontalPadding: 12,

    /** Multiply cell horizontal padding */
    // cellHorizontalPaddingScale: 1,

    /** Spacing between widgets in cells (expand buttons, checkboxes) */
    // cellWidgetSpacing: 4,

    /** Background color when hovering over rows */
    rowHoverColor: 'var(--color-slate-2)',

    /** Background color for odd rows (striping) */
    oddRowBackgroundColor: 'var(--color-slate-1)',

    /** Background color for even rows */
    // evenRowBackgroundColor: 'transparent',

    /** Horizontal row borders */
    rowBorder: true,

    /** Border for cells being edited */
    cellEditingBorder: '1px solid var(--color-brand-5)',

    /** Row group indentation size */
    // rowGroupIndentSize: 24,

    // ═══════════════════════════════════════════════════════════════
    // SELECTION & RANGE
    // ═══════════════════════════════════════════════════════════════

    /** Background color for selected rows */
    // selectedRowBackgroundColor: 'var(--color-brand-3)',

    /** Background color for selected cells */
    // selectedCellBackgroundColor: 'var(--color-brand-2)',

    /** Background for cell range selections (semi-transparent recommended) */
    // rangeSelectionBackgroundColor: 'rgba(var(--color-brand-500-rgb), 0.1)',

    /** Border color for range selections */
    // rangeSelectionBorderColor: 'var(--color-brand-6)',

    /** Border style for range selections */
    // rangeSelectionBorderStyle: 'solid',

    /** Background for cells providing chart data */
    // rangeSelectionChartBackgroundColor: 'rgba(var(--color-blue-500-rgb), 0.1)',

    /** Background for cells providing chart categories */
    // rangeSelectionChartCategoryBackgroundColor: 'rgba(var(--color-green-500-rgb), 0.1)',

    /** Flash color when copying/pasting */
    rangeSelectionHighlightColor: 'var(--color-brand-1)',

    /** Header background when part of range */
    // rangeHeaderHighlightColor: 'rgba(var(--color-brand-500-rgb), 0.1)',

    /** Selected items in multi-select widget */
    // selectCellBackgroundColor: 'var(--color-brand-2)',
    // selectCellBorder: '1px solid var(--color-brand-6)',

    // ═══════════════════════════════════════════════════════════════
    // BORDERS & DIVIDERS
    // ═══════════════════════════════════════════════════════════════

    /** Vertical borders between columns */
    columnBorder: true,

    /** Background when hovering over columns (requires grid option) */
    // columnHoverColor: 'var(--color-slate-2)',

    /** Vertical borders for pinned columns */
    // pinnedColumnBorder: '2px solid var(--color-slate-6)',

    /** Horizontal borders for pinned rows */
    // pinnedRowBorder: '2px solid var(--color-slate-6)',

    /** Borders above footer components (pagination, status bar) */
    // footerRowBorder: '1px solid var(--color-slate-4)',

    /** Borders between side panels */
    // sidePanelBorder: '1px solid var(--color-slate-4)',

    /** Tool panel separator borders */
    // toolPanelSeparatorBorder: '1px solid var(--color-slate-4)',

    // ═══════════════════════════════════════════════════════════════
    // PINNED ROWS
    // ═══════════════════════════════════════════════════════════════

    /** Pinned row background color */
    // pinnedRowBackgroundColor: 'var(--color-slate-2)',

    /** Pinned row text color */
    // pinnedRowTextColor: 'var(--color-slate-12)',

    /** Pinned row font weight */
    // pinnedRowFontWeight: 600,

    /** Background for source row that's pinned */
    // pinnedSourceRowBackgroundColor: 'var(--color-brand-2)',

    /** Text color for source row that's pinned */
    // pinnedSourceRowTextColor: 'var(--color-brand-11)',

    /** Font weight for source row that's pinned */
    // pinnedSourceRowFontWeight: 600,

    // ═══════════════════════════════════════════════════════════════
    // INPUTS & FORM FIELDS
    // ═══════════════════════════════════════════════════════════════

    /** Input background color */
    // inputBackgroundColor: 'var(--color-white)',

    /** Input text color */
    // inputTextColor: 'var(--color-slate-12)',

    /** Input border */
    // inputBorder: '1px solid var(--color-slate-6)',

    /** Input border radius */
    // inputBorderRadius: 4,

    /** Input minimum height */
    // inputHeight: 32,

    /** Input padding start */
    // inputPaddingStart: 8,

    /** Input placeholder text color */
    // inputPlaceholderTextColor: 'var(--color-slate-9)',

    /** Input icon color (search icon) */
    // inputIconColor: 'var(--color-slate-9)',

    /** Focused input background */
    // inputFocusBackgroundColor: 'var(--color-white)',

    /** Focused input text color */
    // inputFocusTextColor: 'var(--color-slate-12)',

    /** Focused input border */
    // inputFocusBorder: '2px solid var(--color-brand-6)',

    /** Focused input shadow */
    // inputFocusShadow: '0 0 0 3px rgba(var(--color-brand-500-rgb), 0.1)',

    /** Invalid input background */
    // inputInvalidBackgroundColor: 'var(--color-red-1)',

    /** Invalid input text color */
    // inputInvalidTextColor: 'var(--color-red-11)',

    /** Invalid input border */
    // inputInvalidBorder: '1px solid var(--color-red-6)',

    /** Disabled input background */
    // inputDisabledBackgroundColor: 'var(--color-slate-3)',

    /** Disabled input text color */
    // inputDisabledTextColor: 'var(--color-slate-9)',

    /** Disabled input border */
    // inputDisabledBorder: '1px solid var(--color-slate-5)',

    /** Picker button (select fields) border */
    pickerButtonBorder: '1px solid var(--facade-input-border-color)',

    /** Picker button focused border */
    pickerButtonFocusBorder: '1px solid var(--color-brand-200)',

    /** Picker button background */
    pickerButtonBackgroundColor: 'var(--facade-input-background-color)',

    /** Picker button focused background */
    pickerButtonFocusBackgroundColor: 'var(--facade-input-background-color)',

    /** Picker dropdown list border */
    pickerListBorder: '1px solid var(--color-slate-4)',

    /** Picker dropdown list background */
    pickerListBackgroundColor: 'var(--color-white)',

    /** Color picker thumb size */
    // colorPickerThumbSize: 16,

    /** Color picker track size */
    // colorPickerTrackSize: 150,

    /** Color picker thumb border width */
    // colorPickerThumbBorderWidth: 2,

    /** Color picker track border radius */
    // colorPickerTrackBorderRadius: 4,

    /** Color picker color border radius */
    // colorPickerColorBorderRadius: 2,

    // ═══════════════════════════════════════════════════════════════
    // BUTTONS
    // ═══════════════════════════════════════════════════════════════

    /** Button text color */
    // buttonTextColor: 'var(--color-slate-12)',

    /** Button font weight */
    // buttonFontWeight: 500,

    /** Button background */
    // buttonBackgroundColor: 'var(--color-slate-3)',

    /** Button border */
    // buttonBorder: '1px solid var(--color-slate-6)',

    /** Button border radius */
    // buttonBorderRadius: 4,

    /** Button horizontal padding */
    // buttonHorizontalPadding: 12,

    /** Button vertical padding */
    // buttonVerticalPadding: 6,

    /** Hovered button text color */
    // buttonHoverTextColor: 'var(--color-slate-12)',

    /** Hovered button background */
    // buttonHoverBackgroundColor: 'var(--color-slate-4)',

    /** Hovered button border */
    // buttonHoverBorder: '1px solid var(--color-slate-7)',

    /** Active button text color */
    // buttonActiveTextColor: 'var(--color-slate-12)',

    /** Active button background */
    // buttonActiveBackgroundColor: 'var(--color-slate-5)',

    /** Active button border */
    // buttonActiveBorder: '1px solid var(--color-slate-7)',

    /** Disabled button text color */
    // buttonDisabledTextColor: 'var(--color-slate-8)',

    /** Disabled button background */
    // buttonDisabledBackgroundColor: 'var(--color-slate-2)',

    /** Disabled button border */
    // buttonDisabledBorder: '1px solid var(--color-slate-5)',

    // ═══════════════════════════════════════════════════════════════
    // CHECKBOXES & TOGGLE BUTTONS
    // ═══════════════════════════════════════════════════════════════

    /** Checkbox border radius */
    checkboxBorderRadius: 2,

    /** Checkbox border width */
    checkboxBorderWidth: 1,

    /** Checked checkbox background */
    checkboxCheckedBackgroundColor: 'var(--color-brand-5)',

    /** Checked checkbox border */
    checkboxCheckedBorderColor: 'var(--color-brand-5)',

    /** Checked checkbox mark color */
    // checkboxCheckedShapeColor: 'var(--color-white)',

    /** Checked checkbox mark image */
    // checkboxCheckedShapeImage: 'data:image/svg+xml;base64,...',

    /** Indeterminate checkbox background */
    checkboxIndeterminateBackgroundColor: 'var(--color-brand-5)',

    /** Indeterminate checkbox border */
    checkboxIndeterminateBorderColor: 'var(--color-brand-5)',

    /** Indeterminate checkbox mark color */
    // checkboxIndeterminateShapeColor: 'var(--color-white)',

    /** Indeterminate checkbox mark image */
    // checkboxIndeterminateShapeImage: 'data:image/svg+xml;base64,...',

    /** Unchecked checkbox background */
    // checkboxUncheckedBackgroundColor: 'var(--color-white)',

    /** Unchecked checkbox border */
    // checkboxUncheckedBorderColor: 'var(--color-slate-7)',

    /** Radio button checked mark image */
    // radioCheckedShapeImage: 'data:image/svg+xml;base64,...',

    /** Toggle button width */
    toggleButtonWidth: 40,

    /** Toggle button height */
    toggleButtonHeight: 40,

    /** Toggle button off background */
    // toggleButtonOffBackgroundColor: 'var(--color-slate-6)',

    /** Toggle button on background */
    // toggleButtonOnBackgroundColor: 'var(--color-brand-9)',

    /** Toggle button switch background */
    // toggleButtonSwitchBackgroundColor: 'var(--color-white)',

    /** Toggle button switch inset */
    // toggleButtonSwitchInset: 2,

    // ═══════════════════════════════════════════════════════════════
    // TABS
    // ═══════════════════════════════════════════════════════════════

    /** Tab background color */
    // tabBackgroundColor: 'transparent',

    /** Tab bar (container) background */
    // tabBarBackgroundColor: 'var(--color-slate-2)',

    /** Border below tab bar */
    // tabBarBorder: '1px solid var(--color-slate-4)',

    /** Tab bar horizontal padding */
    // tabBarHorizontalPadding: 8,

    /** Tab bar top padding */
    // tabBarTopPadding: 4,

    /** Tab top padding */
    // tabTopPadding: 8,

    /** Tab bottom padding */
    // tabBottomPadding: 8,

    /** Tab horizontal padding */
    // tabHorizontalPadding: 12,

    /** Spacing between tabs */
    // tabSpacing: 2,

    /** Tab text color */
    // tabTextColor: 'var(--color-slate-11)',

    /** Hovered tab background */
    // tabHoverBackgroundColor: 'var(--color-slate-3)',

    /** Hovered tab text color */
    // tabHoverTextColor: 'var(--color-slate-12)',

    /** Selected tab background */
    // tabSelectedBackgroundColor: 'var(--color-white)',

    /** Selected tab text color */
    // tabSelectedTextColor: 'var(--color-slate-12)',

    /** Selected tab border color */
    // tabSelectedBorderColor: 'var(--color-brand-9)',

    /** Selected tab border width */
    // tabSelectedBorderWidth: 2,

    /** Selected tab underline color */
    // tabSelectedUnderlineColor: 'var(--color-brand-9)',

    /** Selected tab underline width */
    // tabSelectedUnderlineWidth: 2,

    /** Selected tab underline transition */
    // tabSelectedUnderlineTransitionDuration: 0.2,

    // ═══════════════════════════════════════════════════════════════
    // MENUS & DROPDOWNS
    // ═══════════════════════════════════════════════════════════════

    /** Menu background color - match dropdown white background */
    menuBackgroundColor: 'var(--color-white)',

    /** Menu text color - match dropdown slate-11 text */
    menuTextColor: 'var(--color-slate-11)',

    /** Menu border - match dropdown border-slate-5 */
    menuBorder: '1px solid var(--color-slate-5)',

    /** Menu separator color - match dropdown */
    menuSeparatorColor: 'var(--color-slate-5)',

    /** Height of items in scrolling lists - match dropdown item height (py-1.5 = 12px padding + ~12px text + line height) */
    listItemHeight: 28,

    // ═══════════════════════════════════════════════════════════════
    // TOOLTIPS
    // ═══════════════════════════════════════════════════════════════

    /** Tooltip background */
    tooltipBackgroundColor: 'var(--color-brand-10)',

    /** Tooltip text color */
    tooltipTextColor: 'var(--color-white)',

    /** Tooltip border */
    tooltipBorder: 'none',

    /** Error tooltip background */
    // tooltipErrorBackgroundColor: 'var(--color-red-9)',

    /** Error tooltip text color */
    // tooltipErrorTextColor: 'var(--color-white)',

    /** Error tooltip border */
    // tooltipErrorBorder: 'none',

    // ═══════════════════════════════════════════════════════════════
    // ICONS
    // ═══════════════════════════════════════════════════════════════

    /** Icon size (square icons and icon-buttons) */
    iconSize: 16,

    /** Icon color */
    // iconColor: 'inherit',

    /** Icon button color */
    // iconButtonColor: 'var(--color-slate-11)',

    /** Icon button background */
    // iconButtonBackgroundColor: 'transparent',

    /** Icon button background spread beyond border */
    // iconButtonBackgroundSpread: 4,

    /** Icon button border radius */
    // iconButtonBorderRadius: 2,

    /** Icon button hover color */
    // iconButtonHoverColor: 'var(--color-slate-12)',

    /** Icon button hover background */
    // iconButtonHoverBackgroundColor: 'var(--color-slate-3)',

    /** Active icon button color (e.g. filter button with active filter) */
    // iconButtonActiveColor: 'var(--color-brand-11)',

    /** Active icon button background */
    // iconButtonActiveBackgroundColor: 'transparent',

    /** Active icon button indicator dot color */
    // iconButtonActiveIndicatorColor: 'var(--color-brand-9)',

    // ═══════════════════════════════════════════════════════════════
    // SIDEBAR & SIDE PANELS
    // ═══════════════════════════════════════════════════════════════

    /** Sidebar background */
    // sideBarBackgroundColor: 'var(--color-slate-2)',

    /** Sidebar default width */
    // sideBarPanelWidth: 250,

    /** Side button bar (tabs) background */
    // sideButtonBarBackgroundColor: 'var(--color-slate-3)',

    /** Top padding for side buttons */
    // sideButtonBarTopPadding: 4,

    /** Side button background */
    // sideButtonBackgroundColor: 'transparent',

    /** Side button text color */
    // sideButtonTextColor: 'var(--color-slate-11)',

    /** Side button hover background */
    // sideButtonHoverBackgroundColor: 'var(--color-slate-4)',

    /** Side button hover text color */
    // sideButtonHoverTextColor: 'var(--color-slate-12)',

    /** Selected side button background */
    // sideButtonSelectedBackgroundColor: 'var(--color-white)',

    /** Selected side button text color */
    // sideButtonSelectedTextColor: 'var(--color-slate-12)',

    /** Selected side button underline color */
    // sideButtonSelectedUnderlineColor: 'var(--color-brand-9)',

    /** Selected side button underline width */
    // sideButtonSelectedUnderlineWidth: 2,

    /** Selected side button underline transition */
    // sideButtonSelectedUnderlineTransitionDuration: 0.2,

    /** Side button border */
    // sideButtonBorder: 'none',

    /** Selected side button border */
    // sideButtonSelectedBorder: 'none',

    /** Side button left padding */
    // sideButtonLeftPadding: 12,

    /** Side button right padding */
    // sideButtonRightPadding: 12,

    /** Side button vertical padding */
    // sideButtonVerticalPadding: 8,

    // ═══════════════════════════════════════════════════════════════
    // PANELS & DIALOGS
    // ═══════════════════════════════════════════════════════════════

    /** Panel background (charts, advanced filter) */
    // panelBackgroundColor: 'var(--color-white)',

    /** Panel title bar background */
    // panelTitleBarBackgroundColor: 'var(--color-slate-2)',

    /** Panel title bar text color */
    // panelTitleBarTextColor: 'var(--color-slate-12)',

    /** Panel title bar icon color */
    // panelTitleBarIconColor: 'var(--color-slate-11)',

    /** Panel title bar font weight */
    // panelTitleBarFontWeight: 600,

    /** Panel title bar border */
    // panelTitleBarBorder: '1px solid var(--color-slate-4)',

    /** Dialog border */
    // dialogBorder: '1px solid var(--color-slate-4)',

    // ═══════════════════════════════════════════════════════════════
    // ADVANCED FILTER BUILDER
    // ═══════════════════════════════════════════════════════════════

    /** Advanced filter button bar border */
    // advancedFilterBuilderButtonBarBorder: '1px solid var(--color-slate-4)',

    /** Advanced filter indent size per level */
    // advancedFilterBuilderIndentSize: 16,

    /** Column pill color */
    // advancedFilterBuilderColumnPillColor: 'var(--color-blue-9)',

    /** Join operator pill color (AND/OR) */
    // advancedFilterBuilderJoinPillColor: 'var(--color-purple-9)',

    /** Filter option pill color (equals, contains, etc.) */
    // advancedFilterBuilderOptionPillColor: 'var(--color-green-9)',

    /** Value pill color */
    // advancedFilterBuilderValuePillColor: 'var(--color-orange-9)',

    // ═══════════════════════════════════════════════════════════════
    // CHARTS & INTEGRATED TOOLS
    // ═══════════════════════════════════════════════════════════════

    /** Chart menu label color */
    // chartMenuLabelColor: 'var(--color-slate-11)',

    /** Chart menu panel width */
    // chartMenuPanelWidth: 250,

    // ═══════════════════════════════════════════════════════════════
    // COLUMN DROP COMPONENT
    // ═══════════════════════════════════════════════════════════════

    /** Column pill background in drop zones */
    // columnDropCellBackgroundColor: 'var(--color-slate-3)',

    /** Column pill text color */
    // columnDropCellTextColor: 'var(--color-slate-12)',

    /** Column pill drag handle color */
    // columnDropCellDragHandleColor: 'var(--color-slate-9)',

    /** Column pill border */
    // columnDropCellBorder: '1px solid var(--color-slate-5)',

    // ═══════════════════════════════════════════════════════════════
    // DRAG & DROP
    // ═══════════════════════════════════════════════════════════════

    /** Drag handle color on draggable rows/columns */
    dragHandleColor: 'var(--color-slate-7)',

    /** Drag image background when dragging */
    // dragAndDropImageBackgroundColor: 'var(--color-white)',

    /** Drag image border */
    // dragAndDropImageBorder: '1px solid var(--color-slate-6)',

    /** Drag image border when drop not allowed */
    dragAndDropImageNotAllowedBorder: '1px solid var(--color-red-6)',

    /** Drag image shadow */
    // dragAndDropImageShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',

    // ═══════════════════════════════════════════════════════════════
    // WIDGETS & SPACING
    // ═══════════════════════════════════════════════════════════════

    /** Horizontal padding for widget containers (menus, tool panels) - match dropdown p-2 (8px) */
    widgetContainerHorizontalPadding: 8,

    /** Vertical padding for widget containers - match dropdown p-2 (8px) */
    widgetContainerVerticalPadding: 8,

    /** Horizontal spacing between widgets */
    // widgetHorizontalSpacing: 8,

    /** Vertical spacing between widgets */
    // widgetVerticalSpacing: 4,

    // ═══════════════════════════════════════════════════════════════
    // FILTER PANELS & TOOLS
    // ═══════════════════════════════════════════════════════════════

    /** Filter tool panel group indent */
    // filterToolPanelGroupIndent: 16,

    /** Filter panel apply button color */
    // filterPanelApplyButtonColor: 'var(--color-white)',

    /** Filter panel apply button background */
    // filterPanelApplyButtonBackgroundColor: 'var(--color-brand-9)',

    /** Filter panel subtle color for less prominent elements */
    // filterPanelCardSubtleColor: 'var(--color-slate-9)',

    /** Filter panel subtle hover color */
    // filterPanelCardSubtleHoverColor: 'var(--color-slate-10)',

    /** Set filter indent size for tree data */
    // setFilterIndentSize: 16,

    /** Column select indent size for grouped columns */
    // columnSelectIndentSize: 16,

    // ═══════════════════════════════════════════════════════════════
    // VALUE CHANGES & ANIMATIONS
    // ═══════════════════════════════════════════════════════════════

    /** Color for value decreases in animated cells */
    valueChangeDeltaDownColor: 'var(--color-red-9)',

    /** Color for value increases in animated cells */
    valueChangeDeltaUpColor: 'var(--color-green-9)',

    /** Background flash when cell changes (with enableCellChangeFlash) */
    valueChangeValueHighlightBackgroundColor:
      'rgba(var(--color-yellow-500-rgb), 0.3)',

    // ═══════════════════════════════════════════════════════════════
    // FIND & SEARCH
    // ═══════════════════════════════════════════════════════════════

    /** Find match text color */
    // findMatchColor: 'var(--color-slate-12)',

    /** Find match background */
    // findMatchBackgroundColor: 'var(--color-yellow-4)',

    /** Active find match text color */
    // findActiveMatchColor: 'var(--color-slate-12)',

    /** Active find match background */
    // findActiveMatchBackgroundColor: 'var(--color-yellow-6)',

    // ═══════════════════════════════════════════════════════════════
    // ROW LOADING & SKELETON
    // ═══════════════════════════════════════════════════════════════

    /** Native loading rows; deferred cells use the shared first-paint masks. */
    rowLoadingSkeletonEffectColor: 'var(--color-slate-3)'

    // ═══════════════════════════════════════════════════════════════
    // ROW NUMBERS
    // ═══════════════════════════════════════════════════════════════

    /** Row numbers background when range selects all row cells */
    // rowNumbersSelectedColor: 'var(--color-brand-3)',

    // ═══════════════════════════════════════════════════════════════
    // STATUS BAR
    // ═══════════════════════════════════════════════════════════════

    /** Status bar label text color */
    // statusBarLabelColor: 'var(--color-slate-11)',

    /** Status bar label font weight */
    // statusBarLabelFontWeight: 500,

    /** Status bar value text color */
    // statusBarValueColor: 'var(--color-slate-12)',

    /** Status bar value font weight */
    // statusBarValueFontWeight: 600,

    // ═══════════════════════════════════════════════════════════════
    // PAGINATION
    // ═══════════════════════════════════════════════════════════════

    /** Pagination panel height (defaults to rowHeight or 22px minimum) */
    // paginationPanelHeight: 32,

    // ═══════════════════════════════════════════════════════════════
    // MODAL OVERLAY
    // ═══════════════════════════════════════════════════════════════

    /** Background overlay color for loading indicators */
    // modalOverlayBackgroundColor: 'rgba(255, 255, 255, 0.66)',

    // ═══════════════════════════════════════════════════════════════
    // BATCH EDIT MODE
    // ═══════════════════════════════════════════════════════════════

    /** Cell background in batch edit mode */
    // cellBatchEditBackgroundColor: 'var(--color-yellow-2)',

    /** Cell text color in batch edit mode */
    // cellBatchEditTextColor: 'var(--color-slate-12)',

    /** Row background in batch edit mode */
    // rowBatchEditBackgroundColor: 'var(--color-yellow-1)',

    /** Row text color in batch edit mode */
    // rowBatchEditTextColor: 'var(--color-slate-12)',

    // ═══════════════════════════════════════════════════════════════
    // FULL ROW EDIT
    // ═══════════════════════════════════════════════════════════════

    /** Background for invalid row in full row edit */
    // fullRowEditInvalidBackgroundColor: 'var(--color-red-2)'
  } satisfies Partial<ThemeDefaultParams>)

// Module setup ----------------------------------------------------------------

/** Share the static theme so successive grids reuse the same parameter styles. */
export const gridTheme = createGridTheme()
